import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import io from "socket.io-client";
import { BASE_URL } from "../../redux/ApiEndPoints";
import { clearNotificationsAPI } from "../../redux/features/adminSlice";
import toast from "react-hot-toast";

export default function AdminNotification() {
  const dispatch = useDispatch();
  const { students, courses, metrics } = useSelector((state) => state.admin);
  const auth = useSelector((state) => state.auth);
  const adminName = auth?.full_name || "Admin";

  // Debug auth object

  const [allStudent, setAllStudent] = useState([]);
  const [allCourses, setAllCourses] = useState([]);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const bellRef = useRef();

  // Fetch notifications from API on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch(BASE_URL + "notifications", {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.notifications)) {
          setNotifications(
            data.notifications.map((n) => ({
              ...n,
              timestamp:
                n.created_at || n.timestamp || new Date().toISOString(),
            }))
          );
          setUnreadCount(data.notifications.filter((n) => !n.is_read).length);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };
    if (auth?.token) {
      fetchNotifications();
    } else {
      console.warn("No auth token available");
    }
    return () => {};
  }, [auth?.token, dispatch]);

  // Socket.IO for admin notifications
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });
    socket.on("connect", () => {
      // socket.emit("register-admin", { instructorId: auth?.user_id || auth?.id });
    });
    socket.emit("register-admin", auth?.user_id || "admin");
    socket.on("admin-notification", (notification) => {
      setNotifications((prev) => [
        {
          ...notification,
          timestamp: notification.timestamp || new Date().toISOString(),
          is_read: false,
        },
        ...prev,
      ]);
      setUnreadCount((prev) => prev + 1);
    });
    return () => {
      socket.disconnect();
    };
  }, [auth?.user_id]);

  // Mark notifications as read when dropdown is opened
  useEffect(() => {
    if (dropdownOpen && notifications.length > 0) {
      const unreadIds = notifications
        .filter((n) => !n.is_read)
        .map((n) => n.id)
        .filter(Boolean);
      if (unreadIds.length > 0) {
        fetch(BASE_URL + "notifications/mark-read", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify({ ids: unreadIds }),
        }).then(() => {
          setNotifications((prev) =>
            prev.map((n) =>
              unreadIds.includes(n.id) ? { ...n, is_read: true } : n
            )
          );
        });
      }
      setUnreadCount(0);
    }
  }, [dropdownOpen, notifications, auth?.token]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Get notification icon based on event type
  const getNotificationIcon = (event) => {
    switch (event) {
      // System & Analytics
      case "weekly-summary":
        return "📊";
      
      // User Management
      case "new-student":
        return "👤";
      case "new-instructor":
        return "🎓";
      case "user-activated-by-admin":
        return "✅";
      case "user-deactivated-by-admin":
        return "⏸️";
      case "user-deleted-by-admin":
        return "🗑️";
      case "student-deactivated":
        return "⏸️";
      case "student-activated":
        return "✅";
      case "student-account-deleted":
        return "🗑️";
      
      // Course Management
      case "course-publish-request":
        return "📚";
      case "course-unpublish-request":
        return "📚";
      case "course-approved":
        return "✅";
      case "course-unpublish-declined":
        return "❌";
      case "course-unpublish-approved":
        return "✅";
      
      // Certificate Management
      case "certificate-requested":
        return "📜";
      case "certificate-approved":
        return "✅";
      case "certificate-rejected":
        return "❌";
      
      // Organization Management
      case "new-organization-request":
        return "🏢";
      case "organization-approved":
        return "✅";
      case "organization-rejected":
        return "❌";
      
      // System Notifications
      case "system-maintenance":
        return "🔧";
      case "feature-update":
        return "🆕";
      case "security-alert":
        return "🚨";
      
      // Learning Analytics
      case "learning-milestone-reached":
        return "🏆";
      case "high-engagement-course":
        return "🔥";
      case "low-engagement-course":
        return "📉";
      
      // Payment & Subscription
      case "payment-received":
        return "💰";
      case "subscription-expired":
        return "⏰";
      case "subscription-renewed":
        return "🔄";
      
      // Content Moderation
      case "content-flagged":
        return "🚩";
      case "inappropriate-content":
        return "⚠️";
      
      // AI & Assessment
      case "assessme-completed":
        return "📋";
      case "certifyme-assessment":
        return "🎯";
      case "growth-coordinator-usage":
        return "🤖";
      
      // Notebook LM Analytics
      case "notebook-activity-high":
        return "📝";
      case "flashcard-usage-high":
        return "🎴";
      case "quiz-completion-rate":
        return "🧠";
      
      default:
        return "🔔";
    }
  };

  // Get notification category for styling
  const getNotificationCategory = (event) => {
    // Success notifications (positive actions)
    if (
      [
        "user-activated-by-admin",
        "student-activated",
        "course-approved",
        "course-unpublish-approved",
        "certificate-approved",
        "organization-approved",
        "subscription-renewed",
        "payment-received",
        "learning-milestone-reached",
        "high-engagement-course",
        "feature-update"
      ].includes(event)
    )
      return "success";
    
    // Danger notifications (negative actions)
    if (
      [
        "user-deleted-by-admin",
        "student-account-deleted",
        "course-unpublish-declined",
        "certificate-rejected",
        "organization-rejected",
        "subscription-expired",
        "low-engagement-course",
        "security-alert",
        "inappropriate-content",
        "content-flagged"
      ].includes(event)
    )
      return "danger";
    
    // Warning notifications (attention needed)
    if (
      [
        "user-deactivated-by-admin",
        "student-deactivated",
        "system-maintenance",
        "course-unpublish-request"
      ].includes(event)
    )
      return "warning";
    
    // Info notifications (general updates)
    if (
      [
        "weekly-summary",
        "new-student",
        "new-instructor",
        "new-organization-request",
        "course-publish-request",
        "certificate-requested",
        "assessme-completed",
        "certifyme-assessment",
        "growth-coordinator-usage",
        "notebook-activity-high",
        "flashcard-usage-high",
        "quiz-completion-rate"
      ].includes(event)
    )
      return "info";
    
    return "default";
  };

  const formatNotification = (notif) => {
    let data;
    try {
      data =
        typeof notif.data === "string" ? JSON.parse(notif.data) : notif.data;
    } catch (error) {
      console.error("Failed to parse admin notification data:", error);
      return "Invalid notification data.";
    }

    switch (notif.event) {
      // System & Analytics
      case "weekly-summary":
        return `Weekly Summary: ${data.totalStudents || 0} students, ${data.totalInstructors || 0} instructors, ${data.totalCourses || 0} courses. New this week: ${data.newStudents || 0} students, ${data.newInstructors || 0} instructors, ${data.newCourses || 0} courses.`;
      
      // User Management
      case "new-student":
        return `New student registered: ${data.full_name || 'Unknown'} (${data.email || 'No email'})`;
      case "new-instructor":
        return `New instructor registered: ${data.full_name || 'Unknown'} (${data.email || 'No email'})`;
      case "user-activated-by-admin":
        return `User activated: ${data.userName || data.userId} (${data.userEmail || ''})`;
      case "user-deactivated-by-admin":
        return `User deactivated: ${data.userName || data.userId} (${data.userEmail || ''}) - Reason: ${data.reason || 'No reason provided'}`;
      case "user-deleted-by-admin":
        return `User deleted: ${data.userName || data.userId} (${data.userEmail || ''})`;
      case "student-activated":
        return `Student activated: ${data.studentName || data.studentId} (${data.studentEmail || ''})`;
      case "student-deactivated":
        return `Student deactivated: ${data.studentName || data.studentId} (${data.studentEmail || ''})`;
      case "student-account-deleted":
        return `Student account deleted: ${data.studentName || data.studentId} (${data.studentEmail || ''})`;
      
      // Course Management
      case "course-publish-request":
        return `Course publish request: "${data.courseName || 'Course ID ' + data.courseId}" by Instructor ${data.instructorName || data.instructorId}`;
      case "course-unpublish-request":
        return `Course unpublish request: "${data.courseName || 'Course ID ' + data.courseId}" by Instructor ${data.instructorName || data.instructorId}`;
      case "course-approved":
        return `Course approved: "${data.courseName || 'Course ID ' + data.courseId}" by Instructor ${data.instructorName || data.instructorId}`;
      case "course-unpublish-declined":
        return `Course unpublish request declined: "${data.courseName || 'Course ID ' + data.courseId}" by Instructor ${data.instructorName || data.instructorId}`;
      case "course-unpublish-approved":
        return `Course unpublish approved: "${data.courseName || 'Course ID ' + data.courseId}" by Instructor ${data.instructorName || data.instructorId}`;
      
      // Certificate Management
      case "certificate-requested":
        return `Certificate requested: "${data.courseName || 'Course ID ' + data.courseId}" by Student ${data.studentName || data.studentId}`;
      case "certificate-approved":
        return `Certificate approved: "${data.courseName || 'Course ID ' + data.courseId}" for Student ${data.studentName || data.studentId}`;
      case "certificate-rejected":
        return `Certificate rejected: "${data.courseName || 'Course ID ' + data.courseId}" for Student ${data.studentName || data.studentId} - Reason: ${data.reason || 'No reason provided'}`;
      
      // Organization Management
      case "new-organization-request":
        return `New organization request: ${data.organization_name || 'Unknown'} - Contact: ${data.email || 'No email'}, Industry: ${data.industry_type || 'Unknown'}`;
      case "organization-approved":
        return `Organization approved: ${data.organization_name || 'Unknown'} (${data.email || ''})`;
      case "organization-rejected":
        return `Organization rejected: ${data.organization_name || 'Unknown'} (${data.email || ''}) - Reason: ${data.reason || 'No reason provided'}`;
      
      // System Notifications
      case "system-maintenance":
        return `System maintenance scheduled: ${data.date || 'Soon'} - Duration: ${data.duration || 'Unknown'}`;
      case "feature-update":
        return `New feature available: ${data.featureName || 'Check it out!'} - ${data.description || ''}`;
      case "security-alert":
        return `Security Alert: ${data.alertType || 'Unknown'} - ${data.description || 'Please review immediately'}`;
      
      // Learning Analytics
      case "learning-milestone-reached":
        return `Learning milestone reached: Student ${data.studentName || data.studentId} completed ${data.activities || 0} activities in "${data.courseName || 'Course'}"`;
      case "high-engagement-course":
        return `High engagement course: "${data.courseName || 'Course ID ' + data.courseId}" - ${data.engagementRate || 0}% engagement rate`;
      case "low-engagement-course":
        return `Low engagement course: "${data.courseName || 'Course ID ' + data.courseId}" - ${data.engagementRate || 0}% engagement rate`;
      
      // Payment & Subscription
      case "payment-received":
        return `Payment received: $${data.amount || 0} from ${data.payerName || 'Unknown'} for ${data.service || 'Service'}`;
      case "subscription-expired":
        return `Subscription expired: ${data.subscriberName || 'Unknown'} - Service: ${data.service || 'Unknown'}`;
      case "subscription-renewed":
        return `Subscription renewed: ${data.subscriberName || 'Unknown'} - Service: ${data.service || 'Unknown'}`;
      
      // Content Moderation
      case "content-flagged":
        return `Content flagged: "${data.contentType || 'Unknown'}" by ${data.flaggedBy || 'Unknown'} - Reason: ${data.reason || 'No reason provided'}`;
      case "inappropriate-content":
        return `Inappropriate content detected: "${data.contentType || 'Unknown'}" - Action required`;
      
      // AI & Assessment Analytics
      case "assessme-completed":
        return `AssessMe completed: Student ${data.studentName || data.studentId} scored ${data.score || 0}% in "${data.courseName || 'Course'}"`;
      case "certifyme-assessment":
        return `CertifyMe assessment: Student ${data.studentName || data.studentId} scored ${data.score || 0}% in "${data.courseName || 'Course'}" - ${data.passed ? 'PASSED' : 'FAILED'}`;
      case "growth-coordinator-usage":
        return `Growth Coordinator usage: Student ${data.studentName || data.studentId} interacted ${data.interactionCount || 0} times`;
      
      // Notebook LM Analytics
      case "notebook-activity-high":
        return `High notebook activity: Student ${data.studentName || data.studentId} created ${data.activityCount || 0} items in "${data.courseName || 'Course'}"`;
      case "flashcard-usage-high":
        return `High flashcard usage: Student ${data.studentName || data.studentId} created ${data.flashcardCount || 0} flashcards in "${data.courseName || 'Course'}"`;
      case "quiz-completion-rate":
        return `Quiz completion rate: "${data.courseName || 'Course'}" - ${data.completionRate || 0}% completion rate`;
      
      default:
        return notif.event;
    }
  };

  // Clear all notifications
  const handleClearAll = () => {
    dispatch(clearNotificationsAPI(auth.token))
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "All notifications cleared!");
        setNotifications([]);
        setUnreadCount(0);
      })
      .catch((err) => {
        toast.error(err || "Failed to clear notifications");
      });
  };

  return (
    <>
      <div ref={bellRef} style={{ position: "relative" }}>
        <div
          className="notification-bell-container"
          onClick={() => {
            setDropdownOpen((open) => !open);
          }}
          style={{
            position: "relative",
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            background: "#F05203",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s ease",
            transform: dropdownOpen ? "scale(1.05)" : "scale(1)",
          }}
        >
          <span
            className="icon-bell"
            style={{
              fontSize: "20px",
              color: "#fff",
            }}
          />
          {unreadCount > 0 && (
            <div
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                color: "#fff",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold",
                border: "2px solid #fff",
                animation: "pulse 2s infinite",
              }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </div>
          )}
        </div>
        {dropdownOpen && (
          <div
            className="notification-dropdown"
            style={{
              position: "absolute",
              right: "0",
              top: "56px",
              maxWidth: "400px",
              width: "calc(100vw - 64px)",
              maxHeight: "500px",
              background: "#fff",
              borderRadius: "16px",
              boxShadow:
                "0 20px 40px rgba(0, 0, 0, 0.1), 0 6px 16px rgba(0, 0, 0, 0.08)",
              zIndex: 1000,
              overflow: "hidden",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              animation: "slideIn 0.3s ease-out",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                background: "#F05203",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h4
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#fff",
                  }}
                >
                  Notifications
                </h4>
                <p style={{ margin: 0, fontSize: "14px", opacity: 0.9 }}>
                  {notifications.length}{" "}
                  {notifications.length <= 1
                    ? "notification"
                    : "notifications"}
                </p>
              </div>
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "8px",
                    color: "#fff",
                    padding: "6px 12px",
                    fontSize: "12px",
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                  }
                >
                  Clear All
                </button>
              )}
            </div>
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                padding: "0",
              }}
            >
              {notifications.length === 0 ? (
                <div
                  style={{
                    padding: "40px 24px",
                    textAlign: "center",
                    color: "#9ca3af",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                    🔔
                  </div>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: "500" }}>
                    No notifications yet
                  </p>
                  <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
                    We'll notify you when something happens
                  </p>
                </div>
              ) : (
                notifications.map((notif, idx) => {
                  const category = getNotificationCategory(notif.event);
                  const isUnread = idx < unreadCount;

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: "16px 24px",
                        borderBottom:
                          idx === notifications.length - 1
                            ? "none"
                            : "1px solid #f3f4f6",
                        background: isUnread ? "#f8faff" : "#fff",
                        position: "relative",
                        transition: "background 0.2s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (!isUnread) e.target.style.background = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        if (!isUnread) e.target.style.background = "#fff";
                      }}
                    >
                      {isUnread && (
                        <div
                          style={{
                            position: "absolute",
                            left: "0",
                            top: "0",
                            bottom: "0",
                            width: "4px",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          }}
                        />
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            background:
                              category === "success"
                                ? "#ecfdf5"
                                : category === "danger"
                                ? "#fef2f2"
                                : category === "warning"
                                ? "#fffbeb"
                                : category === "info"
                                ? "#eff6ff"
                                : "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            flexShrink: 0,
                          }}
                        >
                          {getNotificationIcon(notif.event)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              lineHeight: "1.4",
                              color: "#374151",
                              marginBottom: "4px",
                              fontWeight: isUnread ? "500" : "400",
                            }}
                          >
                            {formatNotification(notif)}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#9ca3af",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>⏰</span>
                            {new Date(notif.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes slideIn {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .notification-bell-container:hover {
          transform: scale(1.05) !important;
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4) !important;
        }

        .notification-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .notification-dropdown::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .notification-dropdown::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .notification-dropdown::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </>
  );
}
